import React from "react";
import { Link } from "react-router-dom";
import { BOOKING_URL } from "@/lib/site";
import { BookLink } from "@/components/site/BookLink";
import { Eyebrow, SectionHeading } from "@/components/site/Section";
import { trackEvent } from "@/lib/analytics";
import { POPULAR_SERVICES, BLUEPRINT } from "@/lib/content";
import { useContent } from "@/context/ContentContext";

const pillars = [
  { icon: "♙", title: "Medical Expertise", desc: "Led by an APRN with a safety-first, evidence-based approach" },
  { icon: "♢", title: "Beauty Blueprint™", desc: "A whole-face consultation process before treatment recommendations" },
  { icon: "✓", title: "Natural Results", desc: "Personalized plans that preserve balance, harmony, and confidence" },
];

export default function Home() {
  const { popular = POPULAR_SERVICES } = useContent();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <img
          src="/assets/homepage-room.jpg"
          alt="Seraphina Collective luxury treatment suite"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#FBF6F0]/90" />
        <div className="relative max-w-[1180px] mx-auto px-5 lg:px-10 py-24 lg:py-28 text-center animate-fade-up">
          <img
            src="/assets/seraphina-logo-hero.png"
            alt="Seraphina Collective — Medical Aesthetics, Wellness, Transformation"
            className="w-full max-w-[820px] mx-auto h-auto drop-shadow-sm"
          />
          <div className="flex items-center justify-center gap-4 max-w-[520px] mx-auto my-6 text-[var(--gold)]">
            <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[var(--gold)]" />
            <span className="text-2xl">♢</span>
            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-[var(--gold)]" />
          </div>
          <p className="font-display text-2xl sm:text-3xl text-[var(--charcoal)] leading-snug max-w-3xl mx-auto">
            Natural Results. Personalized Beauty. Medical Expertise.
          </p>
          <p className="mt-4 text-[var(--charcoal)] font-medium max-w-2xl mx-auto leading-relaxed">
            Private, one-on-one medical aesthetics and wellness care designed around your goals.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-5">
            <Link to="/services" data-testid="hero-view-services" className="btn-outline-gold rounded-md px-9 py-4">
              View Services
            </Link>
            <BookLink label="Hero Book Consultation" href={BOOKING_URL} data-testid="hero-book-consultation" className="btn-gold rounded-md px-9 py-4">
              Book Consultation
            </BookLink>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-20 bg-[var(--cream)]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <Eyebrow className="text-center mb-12">Wellness</Eyebrow>
          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((p) => (
              <div key={p.title} className="soft-card rounded-2xl p-8 text-center">
                <div className="text-3xl text-[var(--gold)] mb-4">{p.icon}</div>
                <h3 className="text-xl uppercase tracking-wider font-display mb-3">{p.title}</h3>
                <p className="text-sm text-[var(--charcoal-soft)] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEAUTY BLUEPRINT */}
      <section className="py-20 bg-[var(--cream-2)] border-y border-[var(--line)]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <Eyebrow className="mb-3">Signature Consultation Process</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--charcoal)] leading-tight">
              Every Treatment Starts With a Beauty Blueprint™
            </h2>
            <p className="mt-5 text-[var(--charcoal-soft)] leading-relaxed">
              Not sure what you need? Instead of simply selling treatments, Seraphina Collective evaluates
              your face, skin, goals, and long-term maintenance plan to create a personalized recommendation
              designed around you.
            </p>
            <BookLink label="Blueprint Book Consultation" href={BOOKING_URL} data-testid="blueprint-book-btn" className="btn-gold rounded-full px-7 py-3.5 inline-block mt-8">
              Book Your Consultation
            </BookLink>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {BLUEPRINT.map((b) => (
              <div key={b.title} className="soft-card rounded-xl p-5">
                <h4 className="font-display text-xl text-[var(--charcoal)]">{b.title}</h4>
                <p className="text-sm text-[var(--charcoal-soft)] mt-1">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR SERVICES */}
      <section className="py-20 bg-[var(--cream)]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <SectionHeading eyebrow="Our Most Popular Services" title="Results You'll Love" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {popular.map((s) => (
              <div key={s.name} data-testid={`popular-service-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="soft-card rounded-2xl overflow-hidden group">
                <div className="h-44 overflow-hidden">
                  <img src={s.img} alt={s.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6 text-center">
                  <div className="text-2xl text-[var(--gold)] -mt-9 mb-3 bg-white w-11 h-11 rounded-full mx-auto flex items-center justify-center soft-card">{s.icon}</div>
                  <h3 className="uppercase tracking-wider font-display text-lg">{s.name}</h3>
                  <p className="text-sm text-[var(--charcoal-soft)] mt-2 mb-4 leading-relaxed">{s.desc}</p>
                  <Link
                    to="/services"
                    data-testid={`popular-service-link-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => trackEvent("service_click", s.name)}
                    className="btn-outline-gold rounded-full px-5 py-2.5 inline-block"
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/services" data-testid="view-all-services" className="btn-gold rounded-full px-8 py-3.5 inline-block">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="py-16 bg-[var(--cream-2)] border-y border-[var(--line)]">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
          <div className="soft-card rounded-2xl px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-3xl text-[var(--charcoal)]">Your Best You Starts Here</h3>
              <p className="text-[var(--charcoal-soft)] mt-1">Easy online booking. Real results. Personalized for you.</p>
            </div>
            <BookLink label="CTA Band" href={BOOKING_URL} className="btn-gold rounded-full px-8 py-3.5 whitespace-nowrap">
              Book Appointment
            </BookLink>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-20 bg-[var(--cream)]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div className="rounded-2xl overflow-hidden soft-card">
            <img src="/assets/our-commitment.jpg" alt="Seraphina Medspa treatment" className="w-full h-full object-cover" />
          </div>
          <div>
            <Eyebrow className="mb-3">Why Choose Seraphina?</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--charcoal)] leading-tight">
              Personalized Care. Exceptional Results.
            </h2>
            <p className="mt-5 text-[var(--charcoal-soft)] leading-relaxed">
              At Seraphina Collective, we combine advanced technology with a personalized approach to help
              you look and feel your absolute best.
            </p>
            <Link to="/about" className="btn-outline-gold rounded-full px-7 py-3.5 inline-block mt-8">
              About Seraphina
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
